import { LayoutService } from '../service/layout.service';
import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  Component,
  computed,
  effect,
  inject,
  Input,
  model,
  OnInit,
  Signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { $t } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { PrimeNG } from 'primeng/config';
import { DrawerModule } from 'primeng/drawer';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

const presets = {
  Aura
} as const;

declare type KeyOfType<T> = keyof T extends infer U ? U : never;

@Component({
  selector: 'app-configurator',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SelectButtonModule,
    DrawerModule,
    ToggleSwitchModule,
    RadioButtonModule
  ],
  template: `
    <p-drawer
      [visible]="visible()"
      (onHide)="onDrawerHide()"
      position="right"
      [transitionOptions]="'.3s cubic-bezier(0, 0, 0.2, 1)'"
      styleClass="layout-config-sidebar w-20rem"
      header="Settings"
    >
      <div class="config-section">
        <span class="config-label">Primary</span>
        <div class="swatch-row">
          @for (primaryColor of primaryColors(); track primaryColor.name) {
            <button
              type="button"
              [title]="primaryColor.name"
              (click)="updateColors($event, 'primary', primaryColor)"
              class="swatch-button"
              [style]="{
                'background-color':
                  primaryColor?.name === 'noir'
                    ? 'var(--text-color)'
                    : primaryColor?.palette?.['500']
              }"
            >
              @if (primaryColor.name === selectedPrimaryColor()) {
                <i class="pi pi-check swatch-check"></i>
              }
            </button>
          }
        </div>
      </div>

      <div class="config-section">
        <span class="config-label">Surface</span>
        <div class="swatch-row">
          @for (surface of surfaces; track surface.name) {
            <button
              type="button"
              [title]="surface.name"
              (click)="updateColors($event, 'surface', surface)"
              class="swatch-button"
              [style]="{ 'background-color': surface?.palette?.['500'] }"
            >
              @if (
                selectedSurfaceColor()
                  ? selectedSurfaceColor() === surface.name
                  : darkTheme()
                    ? surface.name === 'zinc'
                    : surface.name === 'slate'
              ) {
                <i class="pi pi-check swatch-check"></i>
              }
            </button>
          }
        </div>
      </div>

      <div class="config-section">
        <span class="config-label">Color Scheme</span>
        <p-selectbutton
          [ngModel]="darkTheme()"
          (ngModelChange)="toggleDarkMode()"
          [options]="themeOptions"
          optionLabel="name"
          optionValue="value"
          [allowEmpty]="false"
        ></p-selectbutton>
      </div>

      <div class="config-section">
        <span class="config-label">Card Style</span>
        <p-selectbutton
          [ngModel]="cardStyle()"
          (ngModelChange)="onCardStyleChange($event)"
          [options]="cardStyleOptions"
          optionLabel="name"
          optionValue="value"
          [allowEmpty]="false"
        />
      </div>

      <div class="config-section">
        <span class="config-label">Menu Theme</span>
        <p-selectbutton
          [ngModel]="menuTheme()"
          (ngModelChange)="onMenuThemeChange($event)"
          [options]="menuThemeOptions"
          optionLabel="name"
          optionValue="value"
          [allowEmpty]="false"
        />
      </div>

      <div class="config-section">
        <span class="config-label">Menu Type</span>
        <div class="menu-type-grid">
          <div class="menu-type-row">
            <div class="menu-type-option">
              <p-radiobutton
                name="menuMode"
                value="static"
                [(ngModel)]="menuMode"
                (ngModelChange)="setMenuMode('static')"
                inputId="static"
              ></p-radiobutton>
              <label for="static">Static</label>
            </div>
            <div class="menu-type-option">
              <p-radiobutton
                name="menuMode"
                value="overlay"
                [(ngModel)]="menuMode"
                (ngModelChange)="setMenuMode('overlay')"
                inputId="overlay"
              ></p-radiobutton>
              <label for="overlay">Overlay</label>
            </div>
          </div>
          <div class="menu-type-row">
            <div class="menu-type-option">
              <p-radiobutton
                name="menuMode"
                value="slim"
                [(ngModel)]="menuMode"
                (ngModelChange)="setMenuMode('slim')"
                inputId="slim"
              ></p-radiobutton>
              <label for="slim">Slim</label>
            </div>
            <div class="menu-type-option">
              <p-radiobutton
                name="menuMode"
                value="compact"
                [(ngModel)]="menuMode"
                (ngModelChange)="setMenuMode('compact')"
                inputId="compact"
              ></p-radiobutton>
              <label for="compact">Compact</label>
            </div>
          </div>
          <div class="menu-type-row">
            <div class="menu-type-option">
              <p-radiobutton
                name="menuMode"
                value="reveal"
                [(ngModel)]="menuMode"
                (ngModelChange)="setMenuMode('reveal')"
                inputId="reveal"
              ></p-radiobutton>
              <label for="reveal">Reveal</label>
            </div>
            <div class="menu-type-option">
              <p-radiobutton
                name="menuMode"
                value="drawer"
                [(ngModel)]="menuMode"
                (ngModelChange)="setMenuMode('drawer')"
                inputId="drawer"
              ></p-radiobutton>
              <label for="drawer">Drawer</label>
            </div>
          </div>
          <div class="menu-type-row">
            <div class="menu-type-option">
              <p-radiobutton
                name="menuMode"
                value="horizontal"
                [(ngModel)]="menuMode"
                (ngModelChange)="setMenuMode('horizontal')"
                inputId="horizontal"
              ></p-radiobutton>
              <label for="horizontal">Horizontal</label>
            </div>
          </div>
        </div>
      </div>
    </p-drawer>
  `,
  styles: [
    `
      .config-section {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        margin-bottom: 1.5rem;
      }

      .config-label {
        font-size: 1rem;
        font-weight: 600;
        color: var(--text-muted-color, #6b7280);
      }

      .swatch-row {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
        padding-top: 0.25rem;
      }

      .swatch-button {
        width: 1.5rem;
        height: 1.5rem;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: box-shadow 0.15s;

        &:hover {
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        }
      }

      .swatch-check {
        color: #ffffff;
      }

      .menu-type-grid {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      .menu-type-row {
        display: flex;
      }

      .menu-type-option {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        width: 50%;
      }
    `
  ]
})
export class AppConfigurator implements OnInit {
  @Input({ transform: booleanAttribute }) simple: boolean = false;

  @Input() location: string = 'app';

  layoutService: LayoutService = inject(LayoutService);

  primeng = inject(PrimeNG);

  presets = Object.keys(presets);

  themeOptions = [
    { name: 'Light', value: false },
    { name: 'Dark', value: true }
  ];

  menuThemeOptions: { name: string; value: string }[] = [];

  // Deliberately does NOT re-apply the theme on init (Diamond's own version
  // calls onPresetChange() here to resync after a preset-switcher UI change).
  // Aline only ships one preset (no switcher), and onPresetChange() rebuilds
  // the theme from the raw Aura preset plus just a color extension - it has
  // no knowledge of MyPreset's own components.datatable/panelmenu overrides
  // from app.config.ts, so calling it here would silently wipe them out on
  // every page load.
  ngOnInit() {
    this.updateMenuThemeOptions();
  }

  constructor() {
    // menuMode/cardStyle are local writable models (needed for the
    // radiobutton/selectbutton two-way bindings), snapshotted once from
    // layoutService at construction time. If the saved layout settings
    // finish loading from the profile *after* this component is already
    // constructed - a real race, since that load is an async HTTP call
    // fired right on login while routing to this page - these locals would
    // otherwise be left showing the stale default instead of the loaded
    // mode. Re-sync them whenever the underlying config changes elsewhere.
    effect(() => {
      const config = this.layoutService.layoutConfig();
      this.menuMode.set(config.menuMode);
      this.cardStyle.set(config.cardStyle);
    });
  }

  updateMenuThemeOptions() {
    if (this.darkTheme()) {
      this.menuThemeOptions = [
        { name: 'Dark', value: 'dark' },
        { name: 'Primary', value: 'primary' }
      ];
    } else {
      this.menuThemeOptions = [
        { name: 'Light', value: 'light' },
        { name: 'Dark', value: 'dark' },
        { name: 'Primary', value: 'primary' }
      ];
    }
  }

  // Surface/primary palette data and the actual theme-application logic
  // (updatePreset/updateSurfacePalette) now live on LayoutService, so it can
  // re-apply a saved color on load without duplicating this here - this
  // component just renders swatches from it and forwards clicks to it.
  surfaces = this.layoutService.surfaces;

  primaryColors = this.layoutService.primaryColors;

  selectedPrimaryColor = computed(() => {
    return this.layoutService.layoutConfig().primary;
  });

  selectedSurfaceColor = computed(() => this.layoutService.layoutConfig().surface);

  menuMode = model(this.layoutService.layoutConfig().menuMode);

  cardStyle = model(this.layoutService.layoutConfig().cardStyle);

  visible: Signal<boolean> = computed(
    () => this.layoutService.layoutState().configSidebarVisible
  );

  darkTheme = computed(() => this.layoutService.layoutConfig().darkTheme);

  cardStyleOptions = [
    { name: 'Transparent', value: 'transparent' },
    { name: 'Filled', value: 'filled' }
  ];

  menuTheme = computed(() => this.layoutService.layoutConfig().menuTheme);

  updateColors(event: any, type: string, color: any) {
    if (type === 'primary') {
      this.layoutService.setPrimaryColor(color.name);
    } else if (type === 'surface') {
      this.layoutService.setSurfaceColor(color.name);
    }

    event.stopPropagation();
  }

  onPresetChange(event: any) {
    const preset = presets.Aura;
    const surfacePalette = this.surfaces.find(
      (s) => s.name === this.selectedSurfaceColor()
    )?.palette;
    $t()
      .preset(preset)
      .preset(this.layoutService.buildPresetExt(this.selectedPrimaryColor()))
      .surfacePalette(surfacePalette)
      .use({ useDefaultOptions: true });
  }

  onDrawerHide() {
    this.layoutService.layoutState.update((prev) => ({
      ...prev,
      configSidebarVisible: false
    }));
  }

  onCardStyleChange(value: string) {
    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      cardStyle: value
    }));
  }

  onMenuThemeChange(value: string) {
    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      menuTheme: value
    }));
  }

  setMenuMode(mode: string) {
    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      menuMode: mode
    }));

    if (this.menuMode() === 'static') {
      this.layoutService.layoutState.update((state) => ({
        ...state,
        staticMenuInactive: false
      }));
    }
  }

  toggleDarkMode() {
    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      darkTheme: !state.darkTheme
    }));
    if (this.darkTheme()) {
      this.setMenuTheme('dark');
    }
    this.updateMenuThemeOptions();
  }

  setMenuTheme(theme: string) {
    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      menuTheme: theme
    }));
  }
}
